import type { paths } from "@securityjourney/svc-idp/dist/gen/idp";

import {
  UpdateTenantIDPRequest,
  CreateTenantIDPRequest,
  TenantIdpSettings,
  ManuallyConfirmEmailRequest,
} from "./idpTypes";
import { OpenApiFetchWrapper } from "../openApiFetchWrapper";

export class IdpAdminClient extends OpenApiFetchWrapper<paths> {
  constructor() {
    super();
  }

  // Get a list of IDPs configured in the platform
  async getIdpList() {
    return await this.get("/svc/idp/list");
  }

  // Get details of a specific IDP by UUID
  async idpDetails(idpUuid: string) {
    return await this.get("/svc/idp/{idp_uuid}/details", {
      params: { path: { idp_uuid: idpUuid } },
    });
  }

  // List all domains configured for login redirect
  async getDomainListForRedirectLogin() {
    return await this.get("/svc/idp/domain/login-redirects");
  }

  // Get the domain for redirect login if it exists
  async getDomainForRedirectLogin(domain: string) {
    return await this.get("/svc/idp/domain/login-redirect", {
      params: { query: { domain } },
    });
  }

  // Add or update a domain for IDP redirect login
  async updateDomainForRedirectLogin(req: { domain: string; idpUuid: string }) {
    return await this.post("/svc/idp/domain/login-redirect", { body: req });
  }

  // Remove a domain from IDP redirect login
  async deleteDomainForRedirectLogin(domain: string) {
    return await this.delete("/svc/idp/domain/login-redirect", {
      params: { query: { domain } },
    });
  }

  // Update tenant IDP settings (enable/disable IDP for tenant)
  async updateTenantIdp(req: UpdateTenantIDPRequest) {
    return await this.patch("/svc/idp/tenant", { body: req });
  }

  // Create a new tenant IDP configuration
  async createTenantIdp(req: CreateTenantIDPRequest) {
    return await this.post("/svc/idp/tenant", { body: req });
  }

  // Get tenant's primary IDP
  async getTenantPrimaryIdp(tenantName: string) {
    return await this.get("/svc/idp/tenant", {
      params: { query: { tenantName } },
    });
  }

  // Get a tenant's settings for a specific IDP
  async getTenantIdpSettings(tenantName: string, idpUuid: string) {
    return await this.get("/svc/idp/tenant/settings", {
      params: { query: { tenant: tenantName, idpUuid } },
    });
  }

  // Create or update a tenant's settings for a specific IDP
  async updateTenantSettings(req: TenantIdpSettings) {
    return await this.put("/svc/idp/tenant/settings", { body: { tenantIdpSettings: req } });
  }

  // Remove a tenant's trust relationship with an IDP
  async removeTenantSettings({ tenant, idpUuid }: { tenant: string; idpUuid: string }) {
    return await this.delete("/svc/idp/{tenant}/{idp_uuid}/settings", {
      params: { path: { tenant, idp_uuid: idpUuid } },
    });
  }

  // Manually confirm a user's email address
  async confirmEmail(req: ManuallyConfirmEmailRequest) {
    return await this.post("/svc/idp/confirm-email", { body: req });
  }

  // Check if an IDP is globally enabled in the Cognito app client
  async isIdpEnabled(providerName: string) {
    return await this.get("/svc/idp/enable", {
      params: { query: { providerName } },
    });
  }

  // Globally enable or disable an IDP in the Cognito app client
  async enableIdp(providerName: string, enable: boolean) {
    return await this.post("/svc/idp/enable", {
      body: { providerName, enable },
    });
  }

  // Get information about a user's Cognito authentication status
  async getUserCognitoInfo(email: string) {
    return await this.post("/svc/idp/user-cognito-info", {
      body: { email },
    });
  }

  // List a tenant's active MCP OAuth client registrations
  async listOAuthClientRegistrations(tenant: string) {
    return await this.get("/svc/idp/{tenant}/oauth-client-registrations", {
      params: { path: { tenant } },
    });
  }

  // Create a tenant-owned MCP OAuth client registration; clientName is an optional display name
  async createOAuthClientRegistration(tenant: string, clientName?: string) {
    return await this.post("/svc/idp/{tenant}/oauth-client-registrations", {
      params: { path: { tenant } },
      body: clientName ? { clientName } : {},
    });
  }

  // Disable (soft delete) a tenant-owned MCP OAuth client registration
  async disableOAuthClientRegistration(tenant: string, clientId: string) {
    return await this.delete("/svc/idp/{tenant}/oauth-client-registrations/{client_id}", {
      params: { path: { tenant, client_id: clientId } },
    });
  }
}
